"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _dcCore = require("dc-core");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = require("react");
var _v = _interopRequireDefault(require("uuid/v4"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * This component is used to get a Global Nav configuration object
 * from its immediate child component. It does this by setting
 * the child's 'onGlobalNavConfig' prop to a function which the
 * child calls with the config object. When this component is
 * mounted, the configuration is set on the GlobalNav provider.
 * When the component is unmounted, the provider is reset to
 * the previous/default configuration.
 */
class GlobalNavCustomizer extends _react.Component {
  constructor(...args) {
    super(...args);
    this.state = {
      globalNavConfig: this.props.config,
      globalNavProvider: null
    };
    this.setGlobalNavConfig = globalNavConfig => {
      const {
        onConfig
      } = this.props;
      this.setState({
        globalNavConfig
      });
      onConfig(this.id, globalNavConfig);
    };
    this.id = (0, _v.default)();
  }
  componentDidMount() {
    if ('nav-config' in _dcCore.providers) {
      _dcCore.providers['nav-config']().then(globalNavProvider => {
        this.setState({
          globalNavProvider
        });
      });
    }
  }
  componentWillReceiveProps({
    config: globalNavConfig
  }) {
    if (globalNavConfig) {
      this.setState({
        globalNavConfig
      });
    }
  }

  /**
   * Called for each time 'setState' is called on this mounted component. If all the
   * state variables are correct (all dependencies are met), this will make sure the
   * GlobalNavProvider is notified of changes to its configuration by the child.
   *
   * @param prevProps
   * @param prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      globalNavConfig: prevConfig,
      globalNavProvider: prevProvider
    } = prevState;
    const {
      globalNavConfig: curConfig,
      globalNavProvider: curProvider
    } = this.state;
    if (!curConfig) {
      return;
    }
    if (prevConfig === curConfig && prevProvider === curProvider) {
      return;
    }
    if (curProvider && typeof curProvider === 'object' && 'setConfig' in curProvider) {
      curProvider.setConfig(this.id, curConfig);
    }
  }

  /**
   * This makes sure that, when unmounting the component, the global nav config is
   * reset to its prior/default state.
   */
  componentWillUnmount() {
    const {
      globalNavProvider
    } = this.state;
    if (globalNavProvider) {
      globalNavProvider.reset(this.id);
    }
  }

  /**
   * Called when the child component determines it needs to set the Global Nav
   * configuration.
   *
   * @param globalNavConfig
   */

  render() {
    const {
      children
    } = this.props;
    if (!children) {
      return null;
    }
    return (0, _react.cloneElement)(_react.Children.only(children), {
      onGlobalNavConfig: this.setGlobalNavConfig
    });
  }
}
GlobalNavCustomizer.propTypes = {
  children: _propTypes.default.element,
  /** Global nav custom config object. */
  config: _propTypes.default.shape({
    /**
     * Component to be rendered on the left side. If not specified, the default will
     * be used.
     */
    left: _propTypes.default.func,
    /**
     * Component to be rendered in the middle section. If not specified, the default
     * will be used.
     */
    middle: _propTypes.default.func,
    /** List of actions which will be placed in the global nav bar */
    actions: _propTypes.default.arrayOf(_propTypes.default.shape({
      /**
       * The action's name or ID. This must only be unique among the set
       * of actions in this config.
       */
      id: _propTypes.default.string.isRequired,
      /** Custom action Component to be rendered */
      action: _propTypes.default.func.isRequired
    }))
  }),
  /** Called when a new config object is being applied by this */
  onConfig: _propTypes.default.func
};
GlobalNavCustomizer.defaultProps = {
  children: null,
  config: null,
  /* This is used during testing only. */
  onConfig() {}
};
var _default = GlobalNavCustomizer;
exports.default = _default;